import React, { useState, useRef, useEffect } from 'react';
import { X, Maximize2 } from 'lucide-react';
import useEscapeKey from '../hooks/useEscapeKey';

const BaseModal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'screen-80',
  showCloseButton = true,
  closeOnBackdrop = true,
  resizable = false,
  className = '',
  headerClassName = '',
  contentClassName = ''
}) => {
  useEscapeKey(isOpen, onClose);

  const [modalSize, setModalSize] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const modalRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    if (isOpen) {
      setModalSize(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!modalRef.current) return;
      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;
      const newWidth = Math.max(400, resizeStartRef.current.width + deltaX);
      const newHeight = Math.max(300, resizeStartRef.current.height + deltaY);
      setModalSize({
        width: Math.min(newWidth, window.innerWidth - 32),
        height: Math.min(newHeight, window.innerHeight - 32)
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!modalRef.current) return;
    const rect = modalRef.current.getBoundingClientRect();
    resizeStartRef.current = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height };
    setIsResizing(true);
  };

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    '3xl': 'max-w-7xl',
    '4xl': 'max-w-[120rem]',
    'screen-80': 'max-w-[80vw]',
    full: 'max-w-full mx-4'
  };

  if (!isOpen) return null;

  const modalStyle = modalSize
    ? { width: `${modalSize.width}px`, height: `${modalSize.height}px`, maxWidth: 'none', maxHeight: 'none' }
    : {};

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${!modalSize ? sizeClasses[size] : ''} ${className} ${!modalSize ? 'max-h-[95vh]' : ''} flex flex-col relative ${isResizing ? 'select-none' : ''}`}
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div className={`px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0 ${headerClassName}`}>
            {title && (
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors ml-auto"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>
        )}

        <div className={`flex-1 overflow-y-auto ${contentClassName}`}>
          {children}
        </div>

        {resizable && (
          <div
            className="absolute bottom-0 right-0 w-8 h-8 cursor-nwse-resize group"
            onMouseDown={handleResizeStart}
            onClick={(e) => e.stopPropagation()}
            title="Drag to resize"
          >
            <div className="absolute bottom-1 right-1 opacity-30 group-hover:opacity-60 transition-opacity">
              <Maximize2 className="h-4 w-4 text-gray-500 dark:text-gray-400 rotate-90" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BaseModal;
